// =============================================================================
// features/admin/document-types/DocumentTypesPage.tsx — /admin/document-types
// (F46-S12).
//
// Por que: o rol de documentos exigidos pelo Banco do Povo muda — por
// produto, por exigência nova do órgão, por simplificação. A API do catálogo
// já existe (F46-S02); esta tela fecha o ciclo sem precisar de deploy a cada
// mudança de rol.
//
// Regras de negócio:
//   - Não existe excluir. Um tipo em uso vira `active: false` — documentos já
//     classificados com aquele tipo continuam apontando para ele; ele só some
//     do seletor de upload de NOVOS documentos (F46-S19).
//   - Ordenação por `sort_order` — campo numérico, sem drag-and-drop (DS:
//     "escolha a forma mais simples que caiba no Design System").
//   - Aviso de impacto ao marcar `required_for` vive no modal (F46-S12,
//     DocumentTypeModal.tsx).
//
// Gate: permissão `documents:manage` (admin-only, ver types.routes.ts) E flag
// `documents.enabled`. A flag nasce OFF no seed (migration 0139) — fail-closed
// por padrão: `canAccessDocumentTypes` só libera com AMBOS positivos, nunca
// um substituindo o outro.
//
// DS:
//   - Tabela densa: elev-1, hover de linha (mesmo padrão de pages/admin/Cities.tsx).
//   - Tipografia: Bricolage (header), Geist (body), JetBrains Mono (key).
//   - Loading: skeleton de linhas (nunca spinner sozinho). Erro: card + retry.
//   - Empty: SVG inline + CTA.
// =============================================================================

import type { DocumentType, DocumentTypeRequiredFor } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import type { FeatureFlagStatus } from '../../../hooks/useFeatureFlag';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';

import { CATEGORY_OPTIONS, DocumentTypeModal, REQUIRED_FOR_OPTIONS } from './DocumentTypeModal';
import { useDocumentTypes, useUpdateDocumentType } from './hooks';

// ─── Permissão + flag exigidas ────────────────────────────────────────────────

export const DOCUMENT_TYPES_PERMISSION = 'documents:manage';
export const DOCUMENTS_FLAG_KEY = 'documents.enabled';

/**
 * Gate fail-closed: só libera a tela com a permissão E a flag ligadas.
 * Nenhum dos dois compensa a ausência do outro — a flag nasce OFF (migration
 * 0139) porque o DPIA de LGPD e o backup off-site ainda não foram concluídos;
 * um admin sem a flag ligada NÃO deve enxergar o catálogo.
 */
export function canAccessDocumentTypes(
  hasManagePermission: boolean,
  flagStatus: FeatureFlagStatus | undefined,
): boolean {
  const flagEnabled = flagStatus === 'enabled' || flagStatus === 'internal_only';
  return hasManagePermission && flagEnabled;
}

// ─── Formatação pura (testada isoladamente) ───────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

const REQUIRED_FOR_LABEL: Record<string, string> = Object.fromEntries(
  REQUIRED_FOR_OPTIONS.map((o) => [o.value, o.label]),
);

export function formatCategory(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}

export function formatRequiredFor(requiredFor: DocumentTypeRequiredFor[]): string {
  if (requiredFor.length === 0) return 'Opcional';
  return requiredFor.map((r) => REQUIRED_FOR_LABEL[r] ?? r).join(' + ');
}

export function formatExpiresAfterDays(days: number | null): string {
  if (days === null) return 'Não vence';
  return `${days} dia${days === 1 ? '' : 's'}`;
}

/** bytes → texto legível em MB (arquivos de documento nunca chegam a GB). */
export function formatMaxSize(bytes: number): string {
  const mb = bytes / 1_048_576;
  const rounded = Math.round(mb * 10) / 10;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} MB`;
}

/** Ordena por `sort_order` asc, com `label` como desempate estável. */
export function sortDocumentTypes(types: DocumentType[]): DocumentType[] {
  return [...types].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.label.localeCompare(b.label, 'pt-BR');
  });
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function TableSkeleton(): React.JSX.Element {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} aria-hidden="true">
          {Array.from({ length: 6 }).map((__, j) => (
            <td key={j} className="px-4 py-4">
              <div
                className="h-4 rounded-xs animate-pulse"
                style={{ width: 60 + ((i * 19 + j * 13) % 90), background: 'var(--surface-muted)' }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }): React.JSX.Element {
  return (
    <tr>
      <td colSpan={6}>
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <svg
            viewBox="0 0 120 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-28 h-auto opacity-40"
            aria-hidden="true"
          >
            <ellipse cx="60" cy="88" rx="46" ry="7" fill="var(--surface-muted)" />
            <rect
              x="35"
              y="18"
              width="50"
              height="62"
              rx="4"
              fill="var(--bg-elev-2)"
              stroke="var(--border-strong)"
              strokeWidth="1.5"
            />
            <path
              d="M43 34h34M43 46h34M43 58h20"
              stroke="var(--border-strong)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>

          <div className="flex flex-col gap-1">
            <p
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.025em' }}
            >
              Nenhum tipo de documento cadastrado
            </p>
            <p className="font-sans text-sm text-ink-3 max-w-xs">
              Cadastre os tipos que compõem o checklist de análises e contratos.
            </p>
          </div>

          <Button variant="primary" onClick={onAdd}>
            Cadastrar primeiro tipo
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ─── Linha da tabela ──────────────────────────────────────────────────────────

function DocumentTypeRow({
  documentType,
  onEdit,
}: {
  documentType: DocumentType;
  onEdit: (dt: DocumentType) => void;
}): React.JSX.Element {
  const { mutate: update, isPending, pendingId } = useUpdateDocumentType();
  const isRowPending = isPending && pendingId === documentType.id;

  return (
    <tr
      className="group border-t border-border-subtle"
      style={{
        transition:
          'background-color var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease)',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'translateY(-1px)';
        el.style.boxShadow = 'var(--elev-2)';
        el.style.position = 'relative';
        el.style.zIndex = '1';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.transform = '';
        el.style.boxShadow = '';
        el.style.position = '';
        el.style.zIndex = '';
      }}
    >
      {/* Rótulo + key */}
      <td className="pl-5 pr-4 py-4">
        <button
          type="button"
          onClick={() => onEdit(documentType)}
          className="flex flex-col items-start text-left focus-visible:outline-none focus-visible:underline"
          title={`Editar ${documentType.label}`}
        >
          <span className="font-sans text-sm font-semibold text-ink group-hover:text-azul transition-colors duration-fast">
            {documentType.label}
          </span>
          <code className="font-mono text-xs text-ink-3">{documentType.key}</code>
        </button>
      </td>

      {/* Categoria */}
      <td className="px-4 py-4">
        <span className="font-sans text-sm text-ink-2">
          {formatCategory(documentType.category)}
        </span>
      </td>

      {/* Obrigatoriedade */}
      <td className="px-4 py-4 hidden md:table-cell">
        <Badge variant={documentType.required_for.length > 0 ? 'warning' : 'neutral'}>
          {formatRequiredFor(documentType.required_for)}
        </Badge>
      </td>

      {/* Validade + tamanho máximo */}
      <td className="px-4 py-4 hidden lg:table-cell">
        <span className="font-mono text-xs text-ink-3">
          {formatExpiresAfterDays(documentType.expires_after_days)} · até{' '}
          {formatMaxSize(documentType.max_size_bytes)}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-4">
        <Badge variant={documentType.active ? 'success' : 'neutral'}>
          {documentType.active ? 'Ativo' : 'Inativo'}
        </Badge>
      </td>

      {/* Ações: editar + toggle active */}
      <td className="px-4 py-4 pr-5">
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={documentType.active}
            aria-label={
              documentType.active
                ? `Inativar ${documentType.label} (some do seletor de upload)`
                : `Ativar ${documentType.label}`
            }
            disabled={isRowPending}
            onClick={() => update({ id: documentType.id, body: { active: !documentType.active } })}
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full',
              'border-2 border-transparent',
              'transition-colors duration-fast ease',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
              'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            style={{
              backgroundColor: documentType.active ? 'var(--brand-verde)' : 'var(--surface-muted)',
            }}
          >
            <span
              className="pointer-events-none block h-4 w-4 rounded-full bg-white transition-transform duration-fast ease"
              style={{
                boxShadow: 'var(--elev-1)',
                transform: documentType.active ? 'translateX(16px)' : 'translateX(0)',
              }}
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            onClick={() => onEdit(documentType)}
            aria-label={`Editar ${documentType.label}`}
            title="Editar"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-sm',
              'text-ink-3 hover:text-azul hover:bg-surface-hover',
              'transition-all duration-fast ease',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
            )}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M13.5 3.5l3 3L6 17H3v-3L13.5 3.5Z" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

/**
 * DocumentTypesPage — /admin/document-types
 * Catálogo administrável de tipos de documento (F46-S12). Sem excluir — só
 * inativar. Requer `documents:manage` + flag `documents.enabled` (as duas —
 * fail-closed).
 */
export function DocumentTypesPage(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const { status: flagStatus, isLoading: flagLoading } = useFeatureFlag(DOCUMENTS_FLAG_KEY);
  const canAccess = canAccessDocumentTypes(hasPermission(DOCUMENT_TYPES_PERMISSION), flagStatus);

  const { data, isLoading, isError, refetch } = useDocumentTypes(canAccess);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<DocumentType | undefined>(undefined);

  function openCreate(): void {
    setEditing(undefined);
    setModalOpen(true);
  }

  function openEdit(dt: DocumentType): void {
    setEditing(dt);
    setModalOpen(true);
  }

  const documentTypes = React.useMemo(() => sortDocumentTypes(data ?? []), [data]);

  // ─── Carregando flag — evita flash de "sem acesso" antes de saber o status ──
  if (flagLoading) {
    return (
      <div className="flex flex-col gap-3" aria-hidden="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-11 rounded-sm animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
          />
        ))}
      </div>
    );
  }

  // ─── Sem acesso: permissão OU flag ausente (fail-closed) ────────────────────
  if (!canAccess) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="flex flex-col items-center gap-3 text-center">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-full"
            style={{ background: 'var(--surface-muted)' }}
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              className="w-6 h-6"
              style={{ color: 'var(--text-4)' }}
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
            </svg>
          </div>
          <p className="font-sans text-sm max-w-xs" style={{ color: 'var(--text-3)' }}>
            Você não tem acesso ao catálogo de tipos de documento.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="flex flex-col gap-6"
        style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) both' }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-3xl)',
                letterSpacing: '-0.04em',
                fontVariationSettings: "'opsz' 48",
              }}
            >
              Tipos de documento
            </h1>
            <p className="font-sans text-sm text-ink-3 mt-1">
              Catálogo de documentos exigidos em análises e contratos. Sem excluir — um tipo em uso
              vira inativo.
            </p>
          </div>

          <Button
            variant="primary"
            onClick={openCreate}
            leftIcon={
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            }
          >
            Novo tipo
          </Button>
        </div>

        {/* Tabela */}
        <div
          className="rounded-md border border-border overflow-hidden"
          style={{
            background: 'var(--bg-elev-1)',
            boxShadow: 'var(--elev-1)',
            animation: 'fade-up var(--dur-slow) var(--ease-out) 0.1s both',
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: 'var(--bg-elev-2)' }}>
                  {[
                    { label: 'Rótulo', className: 'pl-5 pr-4 w-[260px]' },
                    { label: 'Categoria', className: 'px-4 w-[130px]' },
                    { label: 'Exigido para', className: 'px-4 w-[160px] hidden md:table-cell' },
                    { label: 'Validade / tamanho', className: 'px-4 hidden lg:table-cell' },
                    { label: 'Status', className: 'px-4 w-[100px]' },
                    { label: 'Ações', className: 'px-4 w-[100px] text-right pr-5' },
                  ].map((col) => (
                    <th
                      key={col.label}
                      scope="col"
                      className={cn('py-3 font-sans font-bold text-ink-3 text-left', col.className)}
                      style={{
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <TableSkeleton />
                ) : isError ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <div
                        className="inline-flex flex-col items-center gap-2 px-6 py-4 rounded-md"
                        style={{ background: 'var(--danger-bg)' }}
                      >
                        <p className="font-sans text-sm font-medium text-danger">
                          Erro ao carregar tipos de documento.
                        </p>
                        <button
                          type="button"
                          onClick={() => refetch()}
                          className="font-sans text-xs text-azul hover:underline focus-visible:outline-none focus-visible:underline"
                        >
                          Tentar novamente
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : documentTypes.length === 0 ? (
                  <EmptyState onAdd={openCreate} />
                ) : (
                  documentTypes.map((dt) => (
                    <DocumentTypeRow key={dt.id} documentType={dt} onEdit={openEdit} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DocumentTypeModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(undefined);
        }}
        documentType={editing}
      />
    </>
  );
}
